import { useCallback, useEffect, useRef, useState } from "react";

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export function useAccessResource(username, userId, service) {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [grants, setGrants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const generation = useRef(0);
  const actionPending = useRef(false);

  const load = useCallback(
    async ({ silent = false, signal } = {}) => {
      const name = String(username ?? "").trim();
      const requestId = ++generation.current;

      if (!name) {
        setLoading(false);
        setRefreshing(false);
        setError("A creator username is required.");
        return false;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const [optionsResponse, grantsResponse] = await Promise.all([
          service.getOptions(name, { signal }),
          service.getGrants(name, { signal }),
        ]);

        if (signal?.aborted || requestId !== generation.current) {
          return false;
        }

        const options = optionsResponse?.data || {};

        const list = grantsResponse?.data?.grants || [];

        setGroups(Array.isArray(options.groups) ? options.groups : []);

        setItems(
          Array.isArray(options[service.itemsKey])
            ? options[service.itemsKey]
            : [],
        );

        setGrants(
          Array.isArray(list)
            ? list.filter(
                (grant) =>
                  (grant.contentType || service.contentType) ===
                  service.contentType,
              )
            : [],
        );

        setError("");
        return true;
      } catch (err) {
        if (!signal?.aborted && requestId === generation.current) {
          setError(
            errorMessage(err, `Unable to load ${service.contentType} access.`),
          );
        }

        return false;
      } finally {
        if (!signal?.aborted && requestId === generation.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [username, service],
  );

  useEffect(() => {
    const controller = new AbortController();

    setGroups([]);
    setItems([]);
    setGrants([]);

    setError("");
    setNotice("");

    void load({
      signal: controller.signal,
    });

    return () => {
      controller.abort();
      generation.current += 1;
    };
  }, [load, userId]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  const saveGrant = useCallback(
    async (form, editingGrantId = null) => {
      if (actionPending.current) {
        return false;
      }

      actionPending.current = true;

      setSaving(true);
      setError("");
      setNotice("");

      try {
        await service.saveGrant(username, form, editingGrantId);

        const success = await refresh();

        if (success) {
          setNotice(
            editingGrantId
              ? "Access updated successfully."
              : "Access granted successfully.",
          );
        }

        return success;
      } catch (err) {
        setError(errorMessage(err, "Unable to save access."));

        return false;
      } finally {
        actionPending.current = false;
        setSaving(false);
      }
    },
    [username, service, refresh],
  );

  const revokeGrant = useCallback(
    async (grantId) => {
      if (actionPending.current) {
        return false;
      }

      actionPending.current = true;

      setRevokingId(String(grantId));
      setError("");
      setNotice("");

      try {
        await service.revokeGrant(username, grantId);

        const success = await refresh();

        if (success) {
          setNotice("Access revoked successfully.");
        }

        return success;
      } catch (err) {
        setError(errorMessage(err, "Unable to revoke access."));

        return false;
      } finally {
        actionPending.current = false;
        setRevokingId(null);
      }
    },
    [username, service, refresh],
  );

  const clearFeedback = useCallback(() => {
    setError("");
    setNotice("");
  }, []);

  return {
    groups,
    items,
    grants,

    loading,
    refreshing,
    saving,
    revokingId,

    error,
    notice,

    refresh,
    saveGrant,
    revokeGrant,
    clearFeedback,
  };
}
